import {
  AuthenticatedTemplate,
  useMsal,
  useIsAuthenticated,
} from "@azure/msal-react";
import { NavigationBar } from "./NavigationBar";
import Sidebar from "./Sidebar";
import Topbar from "./Topbar";
import { Outlet, useLocation } from "react-router-dom";
import { Alert, Button, Spinner, Typography } from "@material-tailwind/react";
import { useEffect, useCallback, useRef } from "react";
import { useAppDispatch } from "../hooks/app";
import {
  requestCompleted,
  requestStarted,
  resetSession,
} from "../redux/actions/app";
import { useSelector } from "react-redux";
import { RootState } from "../redux/store";
import { getUserDetails } from "../redux/actions/user";
import AlertPopup from "./AlertPopup";
import { loginRequest } from "../authConfig";
import { isSessionExpired } from "../utils";
import { SESSION_EXPIRED_ERROR } from "../redux/actionConstants";
import { useInactivityTimeout } from "../hooks/useInactivityTimeout";
import { InactivityWarningModal } from "./InactivityWarningModal";

export const PageLayout = (props: any) => {
  const { instance, inProgress } = useMsal();
  const activeAccount: any = instance.getActiveAccount();
  const isUserLoggedIn = useIsAuthenticated();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const app = useSelector((state: RootState) => state.app);
  const pathToHide = ["/logout", "/auth"];
  const userDetails = useSelector((state: RootState) => state.user.userDetails);
  const isInactivityExpiredRef = useRef(false);

  useEffect(() => {
    if (inProgress !== "none") {
      let message = "Request is in progress";
      if (inProgress === "login") {
        message = "Login is in progress";
      }
      if (inProgress === "startup") {
        message = "Request is in progress";
      }
      if (inProgress === "logout") {
        message = "Logout is in progress";
      }

      dispatch(requestStarted(message));
    } else {
      dispatch(requestCompleted());
    }
  }, [inProgress]);

  useEffect(() => {
    if (inProgress == "none" && isUserLoggedIn) {
      dispatch(getUserDetails(activeAccount.idTokenClaims.oid));
    }
  }, [inProgress, isUserLoggedIn]);

  // Global click handler to check session expiry on UI clicks
  useEffect(() => {
    // Only add click handler if user is logged in and session is not already expired
    if (!isUserLoggedIn || app.sessionExpired) {
      return;
    }

    const handleClick = (event: MouseEvent) => {
      // Check if session is expired on any click
      console.log("🖱️ UI Click detected - Checking session status...");
      if (isSessionExpired()) {
        // Dispatch session expired action
        console.error("❌ Session expired on UI click - Showing expiry popup");
        dispatch({ type: SESSION_EXPIRED_ERROR });
      }
    };

    // Add event listener to document for all clicks
    document.addEventListener("click", handleClick, true);

    // Cleanup: remove event listener on unmount
    return () => {
      document.removeEventListener("click", handleClick, true);
    };
  }, [isUserLoggedIn, app.sessionExpired, dispatch]);

  const showPageLayout = pathToHide.includes(location.pathname);

  // Handle inactivity timeout warning
  const handleInactivityWarning = useCallback(() => {
    console.log("⚠️ Inactivity warning - user will be logged out soon");
  }, []);

  // Handle inactivity timeout - show session expired popup instead of direct logout
  const handleInactivityTimeout = useCallback(() => {
    console.log(
      "⏰ Inactivity timeout completed - showing session expired popup"
    );
    // Mark that this is due to inactivity
    isInactivityExpiredRef.current = true;
    // Dispatch session expired action to show the popup
    dispatch({ type: SESSION_EXPIRED_ERROR });
  }, [dispatch]);

  // Inactivity timeout hook - only enabled when user is logged in and not on excluded pages
  // Note: Removed location.pathname !== "/" condition to allow inactivity timeout on home page too
  const inactivityEnabled =
    isUserLoggedIn &&
    !app.sessionExpired &&
    !showPageLayout &&
    inProgress === "none";

  // Debug log for inactivity timeout
  useEffect(() => {
    if (inactivityEnabled) {
      console.log("✅ Inactivity timeout is ENABLED");
    } else {
      console.log("❌ Inactivity timeout is DISABLED", {
        isUserLoggedIn,
        sessionExpired: app.sessionExpired,
        showPageLayout,
        pathname: location.pathname,
        inProgress,
      });
    }
  }, [
    inactivityEnabled,
    isUserLoggedIn,
    app.sessionExpired,
    showPageLayout,
    location.pathname,
    inProgress,
  ]);

  const { showWarning, reset: resetInactivityTimer } = useInactivityTimeout({
    enabled: inactivityEnabled,
    onWarning: handleInactivityWarning,
    onTimeout: handleInactivityTimeout,
  });

  // Handle "Stay Logged In" button click
  const handleStayLoggedIn = useCallback(() => {
    console.log("✅ User chose to stay logged in - resetting inactivity timer");
    // Reset inactivity flag
    isInactivityExpiredRef.current = false;
    // Use setTimeout to ensure this happens after the modal closes
    setTimeout(() => {
      resetInactivityTimer();
    }, 100);
  }, [resetInactivityTimer]);

  const onLoginClick = async () => {
    try {
      const isInactivity = isInactivityExpiredRef.current;
      console.log(
        "🔄 Session expired - Clearing cache and forcing fresh login...",
        { isInactivity }
      );

      // Reset the inactivity flag
      isInactivityExpiredRef.current = false;

      // Set active account to null first
      instance.setActiveAccount(null);

      // Get all accounts from MSAL cache for logging
      const accounts = instance.getAllAccounts();
      if (accounts.length > 0) {
        console.log(
          `🗑️ Clearing ${accounts.length} account(s) from MSAL cache via localStorage clear`
        );
      }

      // Clear application-specific data
      localStorage.clear();
      sessionStorage.clear();
      dispatch(resetSession());

      // Small delay to ensure cache is cleared
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Redirect to login page - let the authenticate page handle the loginRedirect
      // This prevents redirect loops
      console.log("🔐 Redirecting to login page...");
      const redirectParam = isInactivity
        ? "inactivity=true"
        : "sessionExpired=true";
      window.location.href = `/login?${redirectParam}`;
    } catch (error) {
      console.error("❌ Error during session expiry login:", error);
      // Fallback: clear everything and redirect to login page
      isInactivityExpiredRef.current = false;
      localStorage.clear();
      sessionStorage.clear();
      dispatch(resetSession());
      window.location.href = "/login?sessionExpired=true";
    }
  };

  return (
    <div className="flex min-w-full w-screen relative">
      <>
        {!showPageLayout && isUserLoggedIn && location.pathname !== "/" && (
          <div className="">
            <Sidebar />
          </div>
        )}
        <div className="wrapper d-flex flex-column min-vh-100 bg-light w-full min-w-[80vw]">
          {!showPageLayout && (
            <div className="min-h-[8vh] bg-[#264390] pr-12 pt-2">
              <Topbar />
            </div>
          )}
          <div
            className={`body flex-grow-1 ${
              isUserLoggedIn && "p-4"
            } max-h-[92vh] overflow-hidden bg-gray-300 `}
          >
            <Outlet context={userDetails} />
          </div>
        </div>
      </>

      {app.loading && (
        <div
          className="absolute w-full h-[100vh] z-1 flex flex-row items-center justify-center "
          style={{ backgroundColor: "rgb(255, 255, 255, 0.8)" }}
        >
          <div className="h-[50px] bg-[#264390] rounded-sm flex flex-row items-center justify-center px-2 shadow-md">
            <Spinner className="h-8 w-8 text-white" color="blue" />
            {app.loadingMessage && (
              <div className="text-white ml-2">{app.loadingMessage}</div>
            )}
          </div>
        </div>
      )}

      {app.isLoading && (
        <div
          className="absolute w-full h-[100vh] z-1 flex flex-row items-center justify-center "
          style={{ backgroundColor: "rgb(255, 255, 255, 0.8)" }}
        >
          <div className="h-[50px] bg-[#264390] rounded-sm flex flex-row items-center justify-center px-2 shadow-md">
            <Spinner className="h-8 w-8 text-white" color="blue" />
          </div>
        </div>
      )}

      {app.alertType && app.alertMessage ? <AlertPopup /> : null}

      {/* Inactivity Warning Modal */}
      <InactivityWarningModal
        isOpen={showWarning}
        onStayLoggedIn={handleStayLoggedIn}
        onTimeout={handleInactivityTimeout}
      />

      {app.sessionExpired && (
        <div
          className="absolute h-[100vh] w-[100%] bg-blue-gray-200  flex items-center justify-center z-[10000]"
          style={{ backgroundColor: "rgb(0,0,0, 0.5)" }}
        >
          <div className=" bg-blue-gray-100 border-4 border-indigo-600 rounded-md px-5 py-3 flex flex-col justify-center items-center">
            <Typography className="font-bold text-4xl text-red-700 mb-2">
              Your session has expired
            </Typography>

            <Typography className="font-bold text-base text-center mb-4">
              {isInactivityExpiredRef.current
                ? "Your session has expired due to inactivity. Please log in again to continue using the app."
                : "Please log in again to continue using app."}
            </Typography>

            <Button
              className="mt-3 bg-indigo-800 w-[40%] text-center"
              color="blue"
              onClick={() => onLoginClick()}
            >
              Login Again
            </Button>
          </div>
        </div>
      )}

      <footer className="w-full py-1 text-center text-xs text-black bg-white/70 border-t border-gray-200 fixed bottom-0 left-0 z-50 backdrop-blur-sm">
        Copyright © 2023 - 2025 HighScope Educational Research Foundation
      </footer>
    </div>
  );
};
